import { ClassNameProps, InputRefProps } from '@/Types';
import React, { useEffect, useRef, useState } from 'react';
import { ErrorTooltip, errorTooltipClass } from '@/Components/Controls/ErrorTooltip/ErrorTooltip';
import cn from 'classnames';
import styles from './LogisticProductSelect.scss';
import { useLogisticProductIcons, useShippingServiceIcons } from './hooks/useLogisticProductSelectIcons';
import { Icon } from '@/Components/Icon/Icon';
import { useFormError, useLocalization } from '@/Hooks';
import { OrderAutomatizationProductModel } from '@/Models/OrderUserProducts/OrderAutomatizationProductModel';
import { Button } from '@/Components/Controls/Button/Button';
import { BUTTON_THEMES } from '@/Components/Controls/Button/ButtonThemes';
import Popover from 'devextreme-react/popover';
import { ScrollView } from 'devextreme-react';

type Props<TFormData extends object> = {
  products: OrderAutomatizationProductModel[];
  onSelect: (p: OrderAutomatizationProductModel) => void;
  currency?: string;
  readonly?: boolean;
} & InputRefProps<HTMLSelectElement, TFormData, string | number> &
  ClassNameProps;

export const LogisticProductSelect = <TFormData extends object>({
  className,
  products,
  onSelect,
  currency,
  readonly = false,
  register,
  error,
  rules,
  name,
}: Props<TFormData>) => {
  const {
    modalWindows: {
      orderLinkWithProduct: {
        Placeholders: { SelectLogisticProduct },
        ShippingContract: { Order },
      },
    },
  } = useLocalization();

  const errorMessage = useFormError(name, error);
  const registerRef = register?.(name, readonly ? {} : rules);

  const { getLogisticProductIconByType } = useLogisticProductIcons();
  const { getShippingServiceIconByType } = useShippingServiceIcons();

  const [isFocused, setIsFocused] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const blockRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (readonly) return;

    if (products.length) {
      setIsFocused(true);
    }
  }, [products]);

  const onBlur = () => {
    if (readonly) return;
    setIsFocused(false);
  };

  const onFocus = () => {
    if (readonly) return;
    setIsFocused(true);
  };

  return (
    <ErrorTooltip error={errorMessage}>
      <div ref={listRef} className={cn(styles.logisticProductSelect, className)}>
        <input
          {...registerRef}
          className={cn(styles.input, errorMessage ? errorTooltipClass : null)}
          autoComplete={'off'}
          placeholder={SelectLogisticProduct}
          onFocus={onFocus}
          onKeyDown={(ev) => {
            if (ev.key == 'Escape') {
              ev.preventDefault();
              setIsFocused(false);
            }
          }}
          readOnly
        />

        <Popover
          maxWidth={550}
          maxHeight={400}
          width={'100%'}
          position="bottom"
          visible={isFocused}
          showCloseButton={false}
          target={listRef.current as Element}
          onHiding={onBlur}
        >
          <ScrollView height={'100%'}>
            <div className={styles.options} ref={blockRef}>
              {products.map((product) => {
                const [from, to] = getShippingServiceIconByType(product.shippingServiceType);

                return (
                  <div key={product.id} className={cn(styles.option)}>
                    <div className={styles.logisticType} title={product.productTypeName}>
                      <Icon src={getLogisticProductIconByType(product.logisticTypeId)} alt={'icon'} />
                      <div>{product.productTypeName}</div>
                    </div>
                    <div className={styles.shippingFrom}>
                      <Icon src={from.icon} alt={'shipping'} />
                      <div>{from.type}</div>
                    </div>
                    <div className={styles.shippingTo}>
                      <Icon src={to.icon} alt={'shipping'} />
                      <div>{to.type}</div>
                    </div>
                    <div className={styles.shippingPrice}>
                      {product.userProductPrice} {currency}
                    </div>
                    <div className={styles.shippingAction}>
                      <Button
                        text={Order}
                        theme={BUTTON_THEMES.SUCCESS}
                        onClick={() => {
                          const input = listRef.current?.children[0] as HTMLInputElement;
                          if (input) {
                            setIsFocused(false);
                            onSelect(product);
                          }
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollView>
        </Popover>
      </div>
    </ErrorTooltip>
  );
};
